import { Edit, UserPlus, X, Eye, EyeOff } from "lucide-react";
import { EditIcon, GreenCircleIcon, RedCircleIcon } from "../utils/Icons";
import { useState, useEffect, useMemo } from "react"; // Add useEffect
import CustomSelectBox from "../components/CustomSelectBox";
import { User } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  addClientUser,
  checkEmailExists,
  editClientUser,
  getUsers,
  setUserStatus,
} from "../services/userService";
import {
  describeUnsupportedUserWrite,
  resolveUserWriteEndpoints,
} from "../services/userEndpoints";
import { Spinner } from "../CommonFunc";
import { usePageConfig } from "../hooks/usePageConfig";
import { OnboardingTour, TOUR_STEPS } from "../onboarding";
import { getSourceName } from "../services/authService";
import { getClientEntry } from "../config/clientConfig";
import { toUserRows, permissionLabelsFor } from "../utils/userRoles";

const roles = [
  "Admin",
  "EE Motor Sales",
  "EE Motor UW",
  "EE Property Sales",
  "EE Property UW",
  "LT Motor Sales",
  "LT Motor UW",
  "LT Property Sales",
  "LT Property UW",
  "LV Motor Sales",
  "LV Motor UW",
  "LV Property Sales",
  "LV Property UW",
];

export default function UserManagement() {
  const { clientConfig } = usePageConfig();
  const clientSourceName = clientConfig.sourceName || "bajaj";

  // Hegic's Add User form is not Ergo's shape with a different role list — it
  // has its own role vocabulary (Reviewer/Surveyor, not EE/LT/LV Motor/
  // Property Sales/UW) and a set of permission checkboxes Ergo has no
  // equivalent of at all. `userForm` in a client's own file (see hegic.json)
  // is what switches this page between the two; every client without it
  // keeps the original Ergo-shaped form untouched.
  const activeClient = useMemo(() => getClientEntry(getSourceName()), []);
  const userForm = activeClient?.userForm;
  const roleOptions = userForm?.roles || roles;
  const permissionOptions = userForm?.permissions || [];

  const [userData, setUserData] = useState([]); // Initialize as empty array
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [currentUser, setCurrentUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: "",
    email: "",
    role: roleOptions[0] || "Admin",
    status: "Active",
    permissions: [],
  });
  const [loading, setLoading] = useState(true); // Add loading state
  // Two separate failures, because they have different blast radii. `error`
  // means the list could not be fetched and there is nothing to show, so it
  // replaces the table. `formError` means one write failed — the list on screen
  // is still correct, so it belongs in the modal and must not touch the table.
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const { t } = useTranslation();

  /**
   * Which user writes this client's backend can actually take.
   *
   * Bajaj and Hegic have an add route and no edit route; Ergo has both. A
   * client with no route for an action gets a disabled control and a reason
   * rather than a button that 404s. Resolved once — it depends on the logged-in
   * client, which cannot change without a remount.
   */
  const userWrites = useMemo(() => resolveUserWriteEndpoints(), []);

  // Fetch users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUsers(); // Assuming getUsers is your fetchClientUsers function

        // toUserRows/permissionLabelsFor (src/utils/userRoles.js) are the one
        // place Ergo's, Bajaj's and Hegic's three different field vocabularies
        // get reconciled — this used to carry its own fourth copy of Ergo's
        // access-level map, inline, which is exactly the duplication that
        // module exists to end.
        const formattedData = toUserRows(data, {
          activeFlagValue: activeClient?.activeFlagValue,
        }).map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email || "-",
          role: user.role || "-",
          permissions: permissionLabelsFor(user.permission, permissionOptions),
          lastModified: user.lastModified || "-",
          status: user.active ? "active" : "inactive",
          // Carried for editClientUser's Ergo-motor-only anc_access_level —
          // a field this page has no control for and must not blank on save.
          _raw: user._raw,
        }));
        setUserData(formattedData);
      } catch (err) {
        console.error("Failed to load users:", err.message, err.response?.data);
        setError(`Failed to fetch users: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
    // Runs once, on mount — permissionOptions comes from the logged-in
    // client's own static config and cannot change without a remount, so
    // there is nothing for this to react to by naming it as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const resetForm = () => {
    setFormData({
      name: "",
      password: "",
      confirmPassword: "",
      email: "",
      role: roleOptions[0] || "Admin",
      status: "active",
      permissions: [],
    });
    setShowPassword(false);
  };

  const togglePermission = (code) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(code)
        ? prev.permissions.filter((c) => c !== code)
        : [...prev.permissions, code],
    }));
  };

  const openAddModal = () => {
    if (!userWrites.add) {
      setFormError(describeUnsupportedUserWrite("add"));
      return;
    }
    resetForm();
    setFormError(null);
    setModalType("add");
    setShowModal(true);
  };

  const openEditModal = (user) => {
    if (!userWrites.edit) {
      setFormError(describeUnsupportedUserWrite("edit"));
      return;
    }
    setFormError(null);
    setCurrentUser(user);
    setFormData({
      name: user.name,
      password: "",
      confirmPassword: "",
      email: user.email,
      role: user.role,
      status: user.status === "active" ? "active" : "inactive",
      permissions: permissionOptions
        .filter((option) => (user.permissions || []).includes(option.label))
        .map((option) => option.code),
    });
    setModalType("edit");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentUser(null);
    setFormError(null);
    resetForm();
  };

  /**
   * Suspend/activate — a narrower, separate capability from edit (see
   * userWrites.status / setUserStatus), so it toggles in place rather than
   * going through the edit modal at all.
   */
  const handleToggleStatus = async (user) => {
    if (!userWrites.status) {
      setFormError(describeUnsupportedUserWrite("status"));
      return;
    }
    const nextActive = user.status !== "active";
    if (!window.confirm(`${nextActive ? "Re-activate" : "Suspend"} ${user.name}?`)) {
      return;
    }
    setStatusUpdatingId(user.id);
    try {
      await setUserStatus(user.id, nextActive);
      setUserData((prev) =>
        prev.map((entry) =>
          entry.id === user.id
            ? { ...entry, status: nextActive ? "active" : "inactive" }
            : entry,
        ),
      );
    } catch (err) {
      console.error("Failed to update user status:", err);
      setFormError(`Failed to update status for ${user.name}: ${err.message}`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getCurrentTimestamp = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = async () => {
    if (
      !formData.name ||
      !formData.email ||
      (modalType === "add" && !formData.password)
    ) {
      setFormError("Please fill in all required fields.");
      return;
    }
    // Only Hegic's form asks for it twice — nothing is sent anywhere with
    // this value, it only guards against a typo in the one field that can't
    // be seen once submitted.
    if (userForm && modalType === "add" && formData.password !== formData.confirmPassword) {
      setFormError("Password and confirm password do not match.");
      return;
    }
    // Nothing to send this to. Say so rather than firing a request at a route
    // the backend does not have and reporting its 404 as a failure to save.
    if (modalType === "add" ? !userWrites.add : !userWrites.edit) {
      setFormError(describeUnsupportedUserWrite(modalType === "add" ? "add" : "edit"));
      return;
    }
    setFormError(null);

    // Only Hegic's backend can answer this (activeClient.emailCheckEndpoint);
    // everywhere else this is skipped entirely. A failed check doesn't block
    // the add — /add_user is still the source of truth for a real duplicate.
    if (modalType === "add" && activeClient?.emailCheckEndpoint) {
      setCheckingEmail(true);
      let emailExists = false;
      try {
        emailExists = await checkEmailExists(formData.email);
      } catch (err) {
        console.error("Email existence check failed:", err);
      } finally {
        setCheckingEmail(false);
      }
      if (emailExists) {
        setFormError("A user with this email already exists.");
        return;
      }
    }

    try {
      const roleToAccessLevel = {
        Admin: "10",
        "EE Motor Sales": "11",
        "EE Motor UW": "12",
        "EE Property Sales": "13",
        "EE Property UW": "14",
        "LT Motor Sales": "15",
        "LT Motor UW": "16",
        "LT Property Sales": "17",
        "LT Property UW": "18",
        "LV Motor Sales": "19",
        "LV Motor UW": "20",
        "LV Property Sales": "21",
        "LV Property UW": "22",
      };
      if (modalType === "add") {
        // Hegic's /add_user (confirmed by a real captured request) takes
        // name/email/password/role/permission, nothing Ergo's shape has
        // except access_level — addClientUser (userService.js) is what
        // actually branches the request body on the client's own `userForm`
        // config; this just has to hand it the right pieces either way.
        const userData = userForm
          ? {
              full_name: formData.name,
              user_id: formData.email,
              password: formData.password,
              role: formData.role.toLowerCase(),
              permissions: formData.permissions,
              access_level: "0",
            }
          : {
              client_id: "1", // Hardcoded as per cURL
              full_name: formData.name,
              client_source_name: clientSourceName,
              access_level: roleToAccessLevel[formData.role] || "10", // Map role to access_level, default to Admin
              user_id: formData.email, // Use email as user_id
              flag: formData.status === "active" ? "1" : "2",
            };

        const newUser = await addClientUser(userData);

        // Add new user to local state (or refetch users)
        setUserData((prev) => [
          ...prev,
          {
            id: String(newUser.id || Date.now()),
            name: newUser.full_name || formData.name,
            email: newUser.user_id || formData.email,
            role: formData.role,
            permissions: permissionOptions
              .filter((option) => formData.permissions.includes(option.code))
              .map((option) => option.label),
            lastModified: newUser.date_updated || getCurrentTimestamp(),
            status:
              newUser.flag !== undefined
                ? newUser.flag === "1"
                  ? "active"
                  : "inactive"
                : formData.status,
          },
        ]);
      } else {
        const updatedUserData = {
          full_name: formData.name,
          user_id: formData.email,
          access_level: roleToAccessLevel[formData.role] || "10",
          flag: formData.status === "active" ? "1" : "2",
          ancAccessLevel: currentUser?._raw?.anc_access_level,
        };

        await editClientUser(currentUser.id, updatedUserData);

        // Update UI immediately after success
        setUserData((prev) =>
          prev.map((user) =>
            user.id === currentUser.id
              ? {
                  ...user,
                  name: formData.name,
                  email: formData.email,
                  role: formData.role,
                  lastModified: getCurrentTimestamp(),
                  status: formData.status.toLowerCase(),
                }
              : user
          )
        );
      }

      closeModal();
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      // Into the modal, not into `error` — that one replaces the whole table,
      // so a failed save used to destroy the list it failed to add to.
      setFormError(
        `Failed to ${modalType === "add" ? "add" : "update"} user: ${
          error.message
        }`
      );
    }
  };

  // Same ratios as the old grid-cols-12 + col-span layout (2:4:2:2:1:1, which
  // is exactly "2fr 4fr 2fr 2fr 1fr 1fr") when there's no Permissions column
  // to make room for, so a client without `userForm` sees a byte-identical
  // table to before this existed.
  const showPermissionsColumn = permissionOptions.length > 0;
  const tableGridStyle = {
    display: "grid",
    gridTemplateColumns: showPermissionsColumn
      ? "2fr 3fr 1.5fr 2.5fr 1.3fr 0.9fr 0.8fr"
      : "2fr 4fr 2fr 2fr 1fr 1fr",
  };

  return (
    <div className="h-full bg-background-primary">
      <OnboardingTour
        tourKey="userManagement"
        steps={TOUR_STEPS.userManagement}
        ready={!loading && userData.length > 0}
      />
      <div className="p-4 sm:p-12 sm:pl-8">
        <div className="mb-6 flex flex-col items-start gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-medium leading-tight text-black sm:text-5xl sm:leading-[54px]">
            {t("userManagement")}
          </h1>
          <button
            onClick={openAddModal}
            disabled={!userWrites.add}
            title={userWrites.add ? undefined : describeUnsupportedUserWrite("add")}
            data-tour="users-add-button"
            className="inline-flex h-12 shrink-0 items-center gap-2 rounded-[32px] bg-white px-6 py-3 disabled:cursor-not-allowed disabled:opacity-50 sm:px-10"
          >
            <UserPlus className="h-6 w-6" />
            <span className="text-base font-semibold leading-normal tracking-[-0.32px] text-base-layer-2">
              {t("add_user")}
            </span>
          </button>
        </div>

        {/* A failed write, while the modal is closed. The table stays put. */}
        {!showModal && formError && (
          <div className="mb-6 rounded-2xl border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">
            {formError}
          </div>
        )}

        {loading ? (
          <div className="text-white h-[calc(100vh_-_260px)] flex items-center justify-center">
            <Spinner className="w-12 h-12" />
          </div>
        ) : error ? (
          <div className="text-red-500 h-[calc(100vh_-_260px)] flex items-center justify-center">
            {error}
          </div>
        ) : (
          <div className="w-full rounded-2xl overflow-hidden">
            <div className="bg-background-primary uppercase px-7 py-5">
              <div style={tableGridStyle} className="gap-9 items-center text-sm text-gray-600">
                <div>
                  <span className="  leading-normal tracking-[-0.32px]">
                    {t("user_name")}
                  </span>
                </div>
                <div>
                  <span className="leading-normal tracking-[-0.32px]">
                    {t("email_id")}
                  </span>
                </div>
                <div>
                  <span className=" leading-normal tracking-[-0.32px]">
                    {t("role")}
                  </span>
                </div>
                {showPermissionsColumn && (
                  <div>
                    <span className=" leading-normal tracking-[-0.32px]">
                      Permissions
                    </span>
                  </div>
                )}
                <div>
                  <span className=" leading-normal tracking-[-0.32px]">
                    {t("last_modified")}
                  </span>
                </div>
                <div>
                  <span className=" leading-normal tracking-[-0.32px] flex justify-center">
                    {t("status")}
                  </span>
                </div>
                <div className="text-right">
                  <span className="  leading-normal tracking-[-0.32px]">
                    {t("edit")}
                  </span>
                </div>
              </div>
            </div>

            <div className="overflow-auto">
              <div className="h-[calc(100vh_-_260px)]">
                {userData.map((user, index) => (
                  <div key={user.id}>
                    <div
                      className={`px-7 py-6 bg-white m-1 rounded-lg ${
                        index === userData.length - 1 ? "rounded-b-2xl" : ""
                      }`}
                    >
                      <div style={tableGridStyle} className="gap-9 items-center">
                        <div>
                          <span className="text-sm font-medium leading-6 tracking-[-0.14px]">
                            {user.name}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium leading-6 tracking-[-0.14px]">
                            {user.email}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium leading-6 tracking-[-0.14px]">
                            {user.role}
                          </span>
                        </div>
                        {showPermissionsColumn && (
                          <div>
                            <span className="text-sm font-medium leading-6 tracking-[-0.14px]">
                              {user.permissions?.length ? user.permissions.join(", ") : "—"}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-sm font-medium leading-6 tracking-[-0.14px]">
                            {user.lastModified}
                          </span>
                        </div>
                        <div className="flex justify-center">
                          {user.status === "active" ? (
                            <GreenCircleIcon />
                          ) : (
                            <RedCircleIcon />
                          )}
                        </div>
                        <div className="flex justify-end items-center gap-4">
                          {userWrites.status && (
                            <button
                              onClick={() => handleToggleStatus(user)}
                              disabled={statusUpdatingId === user.id}
                              title={
                                user.status === "active"
                                  ? `Suspend ${user.name}`
                                  : `Re-activate ${user.name}`
                              }
                              className="text-xs font-semibold uppercase text-black hover:text-gray-300 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {statusUpdatingId === user.id
                                ? "…"
                                : user.status === "active"
                                  ? "Suspend"
                                  : "Activate"}
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(user)}
                            disabled={!userWrites.edit}
                            title={
                              userWrites.edit
                                ? undefined
                                : describeUnsupportedUserWrite("edit")
                            }
                            data-tour={index === 0 ? "users-edit-button" : undefined}
                            className="text-black hover:text-gray-300 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <EditIcon className="h-6 w-6" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            onClick={closeModal}
            className="absolute inset-0 bg-black/50 transition-opacity duration-300 opacity-100"
          ></div>

          <div className="relative bg-background-primary rounded-3xl z-10 transform transition-all duration-300 ease-out opacity-100 scale-100">
            <div className="flex items-center justify-between border-b border-gray-300 px-6 py-6">
              <div className="flex items-center gap-2">
                {modalType === "add" ? (
                  <UserPlus className="h-5 w-5 text-black" />
                ) : (
                  <User className="h-5 w-5 text-black" />
                )}
                <h2 className="text-black text-xl">
                  {modalType === "add"
                    ? t("add_new_user")
                    : t("edit_user_details")}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="text-black hover:text-gray-800"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="py-8 px-6">
              <div className="space-y-5">
                <div className="flex items-center bg-white rounded-2xl py-1.5 px-5">
                  <label className="block w-[110px] text-gray-700 text-xs uppercase">
                    {t("user_name")}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full text-black bg-transparent px-3 py-2 border-none outline-none"
                  />
                </div>
                <div className="flex items-center bg-white rounded-2xl py-1.5 px-5">
                  <label className="text-gray-700 text-xs w-[110px] uppercase">
                    {t("password")}
                  </label>
                  <div className="relative w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full bg-transparent text-black px-3 py-2 rounded border-none outline-none pr-10"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-0 top-1/2 transform -translate-y-1/2 text-blue-400"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {/* Hegic's own form asks twice — nothing else does. */}
                {userForm && modalType === "add" && (
                  <div className="flex items-center bg-white rounded-2xl py-1.5 px-5">
                    <label className="text-gray-700 text-xs w-[110px] uppercase">
                      Confirm Pwd
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full bg-transparent text-black px-3 py-2 rounded border-none outline-none"
                    />
                  </div>
                )}
                <div className="flex items-center bg-white rounded-2xl py-1.5 px-5">
                  <label className="block text-gray-800 text-xs w-[110px] uppercase">
                    {t("email")}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-white text-black px-3 py-2 rounded border-none outline-none"
                  />
                </div>
                <div className="relative">
                  <CustomSelectBox
                    label={t("role")}
                    value={formData.role}
                    options={roleOptions}
                    onChange={(v) => setFormData((p) => ({ ...p, role: v }))}
                  />
                </div>
                {/* Hegic-only: the five permissions its own Add User form
                    offers (see hegic.json's userForm.permissions) — Ergo and
                    Bajaj have no equivalent concept, so this section doesn't
                    exist for them at all. */}
                {permissionOptions.length > 0 && (
                  <div className="rounded-2xl bg-white px-5 py-3">
                    <label className="mb-2 block text-gray-700 text-xs uppercase">
                      Permissions
                    </label>
                    <div className="space-y-2">
                      {permissionOptions.map((option) => (
                        <label
                          key={option.code}
                          className="flex items-center gap-2 text-sm text-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(option.code)}
                            onChange={() => togglePermission(option.code)}
                            className="h-4 w-4"
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {!userForm && modalType !== "add" && (
                  <div className="relative">
                    <CustomSelectBox
                      label={t("status")}
                      value={formData.status}
                      options={["Active", "Inactive"]}
                      onChange={(v) =>
                        setFormData((p) => ({ ...p, status: v?.toLowerCase() }))
                      }
                    />
                  </div>
                )}
              </div>

              {formError && (
                <p className="mt-6 text-sm text-red-600">{formError}</p>
              )}

              <button
                onClick={handleSubmit}
                disabled={checkingEmail}
                className="w-full uppercase mt-8 bg-bla text-white py-3 rounded-2xl font-medium hover:bg-gray-900 bg-black transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {checkingEmail ? "Checking email…" : t("submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
